import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { useAuth } from "../../authContext";

function RegisterForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmitRegister = async (event) => {
    event.preventDefault();
    setError("");

    if (!email || !password) {
      setError("이메일과 비밀번호를 입력해주세요.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("이메일 형식이 올바르지 않습니다.");
      return;
    }
    if (password.length < 8) {
      setError("비밀번호는 최소 8자 이상이어야 합니다.");
      return;
    }
    if (password !== confirmPassword) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }

    setLoading(true);
    try {
      // user_id 자리에 이메일 그대로 — 추후 프로필에서 아이디(@핸들) 설정 가능
      const response = await fetch("/api/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: email,
          user_name: email,
          user_pwd: password,
          email: email,
        }),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        setError(text || "회원가입에 실패하였습니다.");
        setLoading(false);
        return;
      }

      alert("회원가입이 완료되었습니다. 로그인해주세요.");
      navigate("/login");
    } catch (err) {
      console.error("회원가입 실패: ", err);
      setError("서버와 통신 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const pwdState = !confirmPassword
    ? null
    : password === confirmPassword
    ? "match"
    : "mismatch";

  return (
    <div className="register-page">
      <div className="register-card">
        <div className="register-header">
          <h1>회원가입</h1>
          <p>이메일만 있으면 시작할 수 있어요</p>
        </div>

        <form onSubmit={handleSubmitRegister} className="register-form">
          {error && <div className="register-error">{error}</div>}

          <div className="register-field">
            <label htmlFor="email">이메일</label>
            <input
              id="email"
              type="email"
              placeholder="example@email.com"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="register-field">
            <label htmlFor="password">비밀번호</label>
            <input
              id="password"
              type="password"
              placeholder="8자 이상"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="register-field">
            <label htmlFor="confirmPassword">비밀번호 확인</label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="비밀번호 다시 입력"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className={pwdState === "mismatch" ? "input-error" : pwdState === "match" ? "input-match" : ""}
            />
            {pwdState === "mismatch" && (
              <span className="register-hint error">비밀번호가 일치하지 않습니다</span>
            )}
            {pwdState === "match" && (
              <span className="register-hint match">✓ 비밀번호가 일치합니다</span>
            )}
          </div>

          <button type="submit" className="register-submit" disabled={loading}>
            {loading ? "처리 중..." : "가입하기"}
          </button>

          <p className="register-login-link">
            이미 계정이 있으신가요? <a onClick={() => navigate("/login")}>로그인</a>
          </p>

          <p className="register-fineprint">
            이름·아이디·전화번호 등 부가정보는 가입 후 프로필에서 설정할 수 있습니다.
          </p>

          <p className="register-fineprint">
            가입 시 <a href="https://antsnest.co.kr/terms" target="_blank" rel="noopener noreferrer">이용약관</a>과{" "}
            <a href="https://antsnest.co.kr/privacy" target="_blank" rel="noopener noreferrer">개인정보 처리방침</a>에 동의합니다.
          </p>
        </form>
      </div>
    </div>
  );
}

function Login() {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const validateForm = () => {
    if (!userId) {
      setMessage("아이디 또는 이메일을 입력해주세요.");
      return false;
    }
    if (!password) {
      setMessage("비밀번호를 입력해주세요.");
      return false;
    }
    setMessage("");
    return true;
  };

  const handleLoginClick = async () => {
    if (validateForm()) {
      try {
        const response = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, user_pwd: password })
        });

        if (response.ok) {
          const userData = await response.json();
          alert(`${userData.name}님, 로그인에 성공하였습니다!`);
          login(userData);
          navigate('/mypage');
        } else {
          if (response.status === 401) {
            setMessage("아이디 또는 비밀번호가 잘못되었습니다.");
          } else {
            setMessage("로그인에 실패하였습니다. 다시 시도해주세요.");
          }
        }
      } catch (error) {
        console.error('Error:', error);
        setMessage("로그인 요청 중 문제가 발생했습니다.");
      }
      setUserId('');
      setPassword('');
    }
  };

  return (
    <div className="Register">
      <div className="container">
        <div className="login-box">
          <h2>Log In</h2>
          <p id="result">{message}</p>
          <div className="input-login">
            <input
              type="text"
              name="id"
              placeholder="아이디 또는 이메일"
              required
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>
          <div className="input-login">
            <input
              type="password"
              name="password"
              placeholder="비밀번호"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="forgetSec">{/* ... */}</div>
          <div className="loginButton">
            <button type="button" className="loginBtn" onClick={handleLoginClick}>
              로그인
            </button>
          </div>
          <a onClick={() => navigate('/Register')} className="registerLink" style={{ cursor: 'pointer' }}>회원가입</a>
        </div>
      </div>
    </div>
  );
}

export { RegisterForm, Login };
